import React, { useState, useEffect, useCallback } from 'react';
import { TopologyData, SptResult, toShortHostname } from '../TopologyData';
import { computeIsisSpt } from '../utils/pathfinder';

interface Props {
    data: TopologyData;
    onClose: () => void;
    onSptResult: (result: SptResult | null) => void;
    onSimulationMode: (on: boolean) => void;
    onExcludedLinks: (links: Set<string>) => void;
    onExcludedNodes: (nodes: Set<string>) => void;
    onTrafficOverlay: (on: boolean) => void;
}

interface DemoStep {
    title: string;
    description: string;
    detail: string;
}

const STEPS: DemoStep[] = [
    {
        title: 'What is SPT?',
        description:
            'The Shortest Path Tree (SPT) computes the optimal routing tree from a single root router to every other router in the IS-IS domain.',
        detail:
            'It uses Dijkstra\'s algorithm with IS-IS metric as edge weight. The result shows the lowest-cost path from the root to each destination, exactly how an IS-IS router builds its forwarding table.',
    },
    {
        title: 'Select a Root Router',
        description:
            'In this step a router is automatically selected as the SPT root. In normal mode you click any router in the graph to set it as root.',
        detail:
            'The root router is highlighted with a red "ROOT" badge. Every other reachable router shows its cumulative IS-IS cost from the root. Links that belong to the SPT are highlighted in the brand color; non-tree links fade out.',
    },
    {
        title: 'Reading the SPT Result',
        description:
            'Each router displays its total IS-IS cost from the root. Links in the tree are bold and colored; off-tree links are dimmed.',
        detail:
            'A cost of 0 means the root itself. Lower cost = preferred path. If two paths have equal cost, IS-IS uses ECMP (Equal-Cost Multi-Path), but this view shows a single tree.',
    },
    {
        title: 'Simulation Mode',
        description:
            'Simulation mode lets you test "what-if" failure scenarios. Click any router or link to toggle it off, and the SPT recalculates instantly.',
        detail:
            'Disabled routers appear faded with a strikethrough; disabled links become nearly invisible. The SPT is recomputed excluding those elements, showing how traffic would reroute after a failure.',
    },
    {
        title: 'Simulating a Link Failure',
        description:
            'A link is now disabled to demonstrate how the SPT adapts. Notice the cost changes on affected routers and the new tree edges.',
        detail:
            'In production networks, this helps plan maintenance windows by predicting traffic shifts before actually shutting down a link or device.',
    },
    {
        title: 'Traffic Overlay',
        description:
            'The Traffic Overlay colors each link by utilization (throughput / capacity). Combine it with SPT to see which tree links carry the most traffic.',
        detail:
            'Green = under 30% utilization, Yellow = 30-70%, Red = over 70%. Links without traffic data appear gray. This helps identify potential bottlenecks on the shortest path tree.',
    },
    {
        title: 'Demo Complete',
        description:
            'You now know how to use SPT Mode, Simulation, and Traffic Overlay. Close this panel and try it yourself on the live topology.',
        detail:
            'Tip: combine SPT + Simulation + Traffic Overlay to do capacity planning. Disable a high-utilization link and check if the alternate paths can absorb the rerouted traffic.',
    },
];

const SptDemoOverlay: React.FC<Props> = ({
    data,
    onClose,
    onSptResult,
    onSimulationMode,
    onExcludedLinks,
    onExcludedNodes,
    onTrafficOverlay,
}) => {
    const [step, setStep] = useState(0);

    const pickDemoRoot = useCallback((): string => {
        const nodes = data.nodes;
        const rr = nodes.find((n) => n.type === 'route-reflector');
        return rr?.id ?? nodes[0]?.id ?? '';
    }, [data]);

    const pickDemoLink = useCallback((): string => {
        const interSite = data.topology.interSiteLinks;
        if (interSite.length > 0) return interSite[0].id;
        return data.links[0]?.id ?? '';
    }, [data]);

    // Apply demo state per step
    useEffect(() => {
        const root = pickDemoRoot();

        switch (step) {
            case 0:
            case 6:
                onSptResult(null);
                onSimulationMode(false);
                onExcludedLinks(new Set());
                onExcludedNodes(new Set());
                onTrafficOverlay(false);
                break;
            case 1:
            case 2: {
                const spt = computeIsisSpt(data.links, root);
                onSptResult(spt);
                onSimulationMode(false);
                onExcludedLinks(new Set());
                onExcludedNodes(new Set());
                onTrafficOverlay(false);
                break;
            }
            case 3: {
                const spt = computeIsisSpt(data.links, root);
                onSptResult(spt);
                onSimulationMode(true);
                onExcludedLinks(new Set());
                onExcludedNodes(new Set());
                onTrafficOverlay(false);
                break;
            }
            case 4: {
                const failLink = pickDemoLink();
                const excluded = new Set([failLink]);
                const spt = computeIsisSpt(data.links, root, excluded);
                onSptResult(spt);
                onSimulationMode(true);
                onExcludedLinks(excluded);
                onExcludedNodes(new Set());
                onTrafficOverlay(false);
                break;
            }
            case 5: {
                const spt = computeIsisSpt(data.links, root);
                onSptResult(spt);
                onSimulationMode(false);
                onExcludedLinks(new Set());
                onExcludedNodes(new Set());
                onTrafficOverlay(true);
                break;
            }
            default:
                break;
        }
    }, [step, data, pickDemoRoot, pickDemoLink, onSptResult, onSimulationMode, onExcludedLinks, onExcludedNodes, onTrafficOverlay]);

    const current = STEPS[step];
    const isFirst = step === 0;
    const isLast = step === STEPS.length - 1;

    const demoRoot = pickDemoRoot();
    const demoRootLabel = demoRoot ? toShortHostname(demoRoot) : 'N/A';

    return (
        <div className="absolute inset-0 z-50 pointer-events-none">
            {/* Semi-transparent scrim */}
            <button
                type="button"
                tabIndex={-1}
                className="absolute inset-0 pointer-events-auto border-none cursor-default"
                style={{ background: 'rgba(0,0,0,0.15)' }}
                onClick={onClose}
                aria-label="Close demo overlay"
            />

            {/* Panel */}
            <div
                className="absolute top-4 right-4 pointer-events-auto
                    w-[380px] rounded-xl shadow-2xl overflow-hidden
                    border border-semantic-border-secondary"
                style={{ background: 'var(--color-semantic-bg-primary)' }}
            >
                {/* Header */}
                <div
                    className="flex items-center justify-between px-5 py-3
                        border-b border-semantic-border-secondary"
                    style={{ background: 'var(--color-semantic-bg-secondary)' }}
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="inline-flex items-center justify-center w-6 h-6
                                rounded-full text-[11px] font-bold"
                            style={{
                                background: 'var(--color-semantic-bg-brand-primary)',
                                color: 'var(--color-semantic-text-brand)',
                            }}
                        >
                            {step + 1}
                        </span>
                        <span className="text-xs font-bold text-semantic-text-primary uppercase tracking-wide">
                            SPT Demo
                        </span>
                        <span className="text-[10px] text-semantic-text-secondary">
                            {step + 1}/{STEPS.length}
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-semantic-text-secondary hover:text-semantic-text-primary
                            transition-colors p-1"
                    >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                            stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>

                {/* Progress bar */}
                <div className="h-0.5 bg-semantic-bg-secondary">
                    <div
                        className="h-full transition-all duration-300"
                        style={{
                            width: `${((step + 1) / STEPS.length) * 100}%`,
                            background: 'var(--color-semantic-text-brand)',
                        }}
                    />
                </div>

                {/* Body */}
                <div className="px-5 py-4 flex flex-col gap-3">
                    <h3 className="text-sm font-bold text-semantic-text-primary">
                        {current.title}
                    </h3>
                    <p className="text-xs leading-relaxed text-semantic-text-primary">
                        {current.description}
                    </p>

                    {/* Context badge for steps that show a root */}
                    {step >= 1 && step <= 5 && (
                        <div
                            className="flex items-center gap-2 px-3 py-2 rounded-lg text-[11px]"
                            style={{ background: 'var(--color-semantic-bg-brand-primary)' }}
                        >
                            <span className="font-semibold text-semantic-text-brand">
                                Root:
                            </span>
                            <span className="font-mono text-semantic-text-brand">
                                {demoRootLabel}
                            </span>
                        </div>
                    )}

                    <div
                        className="px-3 py-2.5 rounded-lg text-[11px] leading-relaxed
                            text-semantic-text-secondary"
                        style={{ background: 'var(--color-semantic-bg-secondary)' }}
                    >
                        {current.detail}
                    </div>
                </div>

                {/* Footer */}
                <div
                    className="flex items-center justify-between px-5 py-3
                        border-t border-semantic-border-secondary"
                >
                    <button
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={isFirst}
                        className={`px-4 py-1.5 text-xs font-semibold rounded-lg
                            border transition-colors ${
                                isFirst
                                    ? 'border-semantic-border-secondary text-semantic-text-secondary/40 cursor-not-allowed'
                                    : 'border-semantic-border-secondary text-semantic-text-secondary hover:text-semantic-text-primary hover:border-semantic-border-primary'
                            }`}
                    >
                        Back
                    </button>
                    {isLast ? (
                        <button
                            onClick={onClose}
                            className="px-4 py-1.5 text-xs font-bold rounded-lg
                                transition-colors"
                            style={{
                                background: 'var(--color-semantic-text-brand)',
                                color: 'var(--color-semantic-bg-primary)',
                            }}
                        >
                            Close Demo
                        </button>
                    ) : (
                        <button
                            onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                            className="px-4 py-1.5 text-xs font-bold rounded-lg
                                transition-colors"
                            style={{
                                background: 'var(--color-semantic-text-brand)',
                                color: 'var(--color-semantic-bg-primary)',
                            }}
                        >
                            Next
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default SptDemoOverlay;
